/**
 * Documents Library Page — Consumer Portal (Borrower Library redesign)
 *
 * Surfaces the consumer's documents library across all loans, with:
 * - Smart-link banner (Astor) when unlinked documents exist.
 * - Predicted-docs killer feature (NEW): proactive docs Astor expects next.
 * - Drag-drop zone for any-time uploads.
 * - Filter pills (All / Verified / Pending / Expired) + name search.
 * - DocumentRow per row with thumbnail + status chip + per-row actions.
 * - Astor Q&A block at the bottom — chat with your documents.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Screen 2)
 *
 * Rules:
 * - Glass variants only (rule 11b)
 * - sx + theme tokens only (rule 11c)
 * - All copy via useI18n() / t() (rule 13)
 * - White-label safe (rule 11d)
 * - Dialog only for confirmations; uploads inline via DragDropZone (rule 14)
 * - 44px touch targets everywhere (portals.md)
 */

import DocIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  EmptyState,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  GlassTextField,
  InputAdornment,
  iconSizes,
  MagicSelect,
  ShimmerLoader,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentQABlock } from '../../components/documents/DocumentQABlock';
import { DocumentRow, type DocumentRowStatus } from '../../components/documents/DocumentRow';
import { DragDropZone } from '../../components/documents/DragDropZone';
import { type PredictedDoc, PredictedDocsBlock } from '../../components/documents/PredictedDocsBlock';
import { SmartLinkBanner } from '../../components/documents/SmartLinkBanner';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { type ConsumerDocument, useCases, useDocuments, useLinkToCase, useUploadDocument } from '../../hooks';
import { useAstorPredictedDocs } from '../../lib/trpc/hooks';
import { formatFileSize } from '../../lib/utils/format-number';

type FilterPill = 'all' | 'verified' | 'pending' | 'expired';

const DOCUMENT_CATEGORY_OPTIONS = [
  { value: 'identity', labelKey: 'documents.category.identity' },
  { value: 'financial', labelKey: 'documents.category.financial' },
  { value: 'property', labelKey: 'documents.category.property' },
  { value: 'legal', labelKey: 'documents.category.legal' },
  { value: 'loan', labelKey: 'documents.category.loan' },
  { value: 'authorization', labelKey: 'documents.category.authorization' },
  { value: 'other', labelKey: 'documents.category.other' },
] as const;

function mapProcessingStatus(status: string): DocumentRowStatus {
  const normalized = status.toLowerCase();
  if (normalized === 'completed' || normalized === 'verified') return 'verified';
  if (normalized === 'failed' || normalized === 'expired') return 'expired';
  return 'pending-review';
}

function mimeToType(mime: string, name: string): 'pdf' | 'xlsx' | 'doc' | 'image' | 'other' {
  const ext = name.split('.').pop()?.toLowerCase();
  if (mime.includes('pdf') || ext === 'pdf') return 'pdf';
  if (mime.includes('sheet') || ext === 'xls' || ext === 'xlsx') return 'xlsx';
  if (mime.includes('word') || ext === 'doc' || ext === 'docx') return 'doc';
  if (mime.startsWith('image/') || (ext && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))) return 'image';
  return 'other';
}

function formatRelativeDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function shortMonoId(linkedCaseNumber?: string): string | undefined {
  if (!linkedCaseNumber) return undefined;
  return linkedCaseNumber.split('-').slice(0, 2).join('-');
}

export default function DocumentsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  // Data
  const { data: docsResponse, isPending, error } = useDocuments(1, 100);
  const uploadMutation = useUploadDocument();
  const linkToCaseMutation = useLinkToCase();

  // Local UI state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadCaseId, setUploadCaseId] = useState<string | undefined>(undefined);
  const [linkDocumentId, setLinkDocumentId] = useState<string | null>(null);
  const [linkCaseId, setLinkCaseId] = useState<string>('');
  const [filterPill, setFilterPill] = useState<FilterPill>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Cases — fetched unconditionally now that the predicted-docs panel
  // needs a case ID to query against. Previously gated on dialog open,
  // but the killer-feature panel renders on every visit so we pay this
  // cost upfront (one page-load, small response).
  const { data: casesResponse, isLoading: casesLoading } = useCases(1, 50);
  const cases = (casesResponse?.cases ?? []) as Array<{
    id: string;
    title: string;
    caseNumber: string;
    status: string;
  }>;
  const consumerId = docsResponse?.consumerId;

  const documents = (docsResponse?.documents ?? []) as ConsumerDocument[];

  // Pick the borrower's most-relevant active case for predicted-docs.
  // Heuristic: first non-closed case in the list. Server already orders
  // by recency, so this defaults to "the loan they're currently working on".
  const predictedDocsCaseId = useMemo<string | null>(() => {
    const closedStatuses = new Set(['closed', 'denied', 'withdrawn', 'archived']);
    const active = cases.find((c) => !closedStatuses.has((c.status ?? '').toLowerCase()));
    return active?.id ?? null;
  }, [cases]);

  const predictedDocsQuery = useAstorPredictedDocs(predictedDocsCaseId);
  const predictedDocs: PredictedDoc[] = useMemo(() => {
    const rows = predictedDocsQuery.data?.predictions ?? [];
    return rows.map<PredictedDoc>((p) => ({
      id: p.id,
      name: p.name,
      confidence: p.confidence,
      reason: p.reason,
    }));
  }, [predictedDocsQuery.data?.predictions]);

  const unlinkedCount = useMemo(
    () => documents.filter((d) => !d.caseLinks || d.caseLinks.length === 0).length,
    [documents],
  );

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      if (searchQuery && !doc.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterPill === 'all') return true;
      const status = mapProcessingStatus(doc.processingStatus);
      if (filterPill === 'verified') return status === 'verified';
      if (filterPill === 'expired') return status === 'expired';
      return status === 'pending-review';
    });
  }, [documents, searchQuery, filterPill]);

  const openUploadDialog = (files: File[]) => {
    setPendingFiles(files);
    setUploadCategory('other');
    setUploadCaseId(undefined);
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    try {
      for (const file of pendingFiles) {
        await uploadMutation.mutateAsync({
          file,
          caseId: uploadCaseId,
          category: uploadCategory,
        });
      }
      setUploadDialogOpen(false);
      setPendingFiles([]);
    } catch (err) {
      // Surface via the mutation's error state (rendered below).
      console.error('Upload failed:', err);
    }
  };

  const handleLinkToCase = async () => {
    if (!linkDocumentId || !linkCaseId || !consumerId) return;
    try {
      await linkToCaseMutation.mutateAsync({
        documentId: linkDocumentId,
        caseId: linkCaseId,
        sourceEntityType: 'CONSUMER',
        sourceEntityId: consumerId,
      });
      setLinkDocumentId(null);
      setLinkCaseId('');
    } catch (err) {
      console.error('Link to case failed:', err);
    }
  };

  const filterPills: Array<{ value: FilterPill; labelKey: string }> = [
    { value: 'all', labelKey: 'consumer.documents.filterAll' },
    { value: 'verified', labelKey: 'consumer.documents.filterVerified' },
    { value: 'pending', labelKey: 'consumer.documents.filterPending' },
    { value: 'expired', labelKey: 'consumer.documents.filterExpired' },
  ];

  if (error) {
    return (
      <PageTemplate variant="list" titleKey="consumer.documents.pageTitle" icon={FolderIcon}>
        <GlassAlert severity="error" visible={true}>
          {t('errors.uploadFailed')}: {error.message}
        </GlassAlert>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      variant="list"
      titleKey="consumer.documents.pageTitle"
      descriptionKey="consumer.documents.pageSubtitle"
      icon={FolderIcon}
      actions={
        <GlassButton
          variant="gradient"
          startIcon={<UploadIcon />}
          onClick={() => openUploadDialog([])}
          sx={{ minHeight: 44 }}
        >
          {t('consumer.documents.uploadButton')}
        </GlassButton>
      }
    >
      <Stack spacing={3} sx={{ pt: 2 }}>
        {uploadMutation.error && (
          <GlassAlert severity="error" visible={true}>
            {t('errors.uploadFailed')}: {uploadMutation.error.message}
          </GlassAlert>
        )}

        {unlinkedCount > 0 && (
          <SmartLinkBanner
            unlinkedCount={unlinkedCount}
            onReviewAndLink={() => {
              // Focus the first unlinked doc's link-to-case flow.
              const firstUnlinked = documents.find((d) => !d.caseLinks || d.caseLinks.length === 0);
              if (firstUnlinked) {
                setLinkDocumentId(firstUnlinked.documentId);
                setLinkCaseId('');
              }
            }}
          />
        )}

        <PredictedDocsBlock
          predictions={predictedDocs}
          onPreStage={() => {
            // Pre-stage = "start the upload dialog with this case pre-selected".
            // The full pre-stage flow (reserve a slot, send a reminder) lands
            // in a follow-up PR; the upload dialog suffices for v1.
            if (predictedDocsCaseId) {
              setUploadCaseId(predictedDocsCaseId);
            }
            openUploadDialog([]);
          }}
        />

        <DragDropZone onFiles={openUploadDialog} />

        <GlassCard sx={{ p: 0 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{
              alignItems: { md: 'center' },
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, flex: { md: 1 } }}>
              {t('consumer.documents.sectionTitle')}
            </Typography>

            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              {filterPills.map((pill) => (
                <GlassChip
                  key={pill.value}
                  label={t(pill.labelKey)}
                  size="small"
                  clickable
                  onClick={() => setFilterPill(pill.value)}
                  color={filterPill === pill.value ? 'success' : 'default'}
                  variant={filterPill === pill.value ? 'filled' : 'outlined'}
                  sx={{ minHeight: 44 }}
                />
              ))}
            </Stack>

            <GlassTextField
              size="small"
              placeholder={t('consumer.documents.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: iconSizes.sm }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ minWidth: { md: 220 } }}
            />
          </Stack>

          {isPending ? (
            <Stack spacing={1} sx={{ p: 2 }}>
              {[0, 1, 2, 3].map((i) => (
                <ShimmerLoader key={i} variant="rectangular" width="100%" height={56} />
              ))}
            </Stack>
          ) : filteredDocuments.length === 0 ? (
            <EmptyState
              icon={<DocIcon sx={{ fontSize: iconSizes['3xl'], color: 'text.disabled' }} aria-hidden="true" />}
              title={
                searchQuery || filterPill !== 'all' ? t('documents.empty.noMatch') : t('documents.empty.noDocuments')
              }
              action={
                searchQuery || filterPill !== 'all' ? (
                  <GlassButton
                    variant="text"
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      setFilterPill('all');
                    }}
                  >
                    {t('common.clearFilters')}
                  </GlassButton>
                ) : undefined
              }
            />
          ) : (
            <Box>
              {filteredDocuments.map((doc) => {
                const firstCaseLink = doc.caseLinks?.[0];
                return (
                  <DocumentRow
                    key={doc.id}
                    id={doc.id}
                    name={doc.name}
                    size={formatFileSize(doc.size)}
                    uploadedAt={formatRelativeDate(doc.uploadedAt)}
                    monoId={shortMonoId(firstCaseLink?.caseNumber)}
                    type={mimeToType(doc.mimeType, doc.name)}
                    status={mapProcessingStatus(doc.processingStatus)}
                    tags={doc.documentType ? [doc.documentType] : []}
                    onPreview={() => navigate(`/documents/${doc.documentId}/view`)}
                    onDownload={() => navigate(`/documents/${doc.documentId}/view`)}
                    onMore={
                      cases.length > 0 || !firstCaseLink
                        ? () => {
                            setLinkDocumentId(doc.documentId);
                            setLinkCaseId('');
                          }
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          )}
        </GlassCard>

        <DocumentQABlock
          onTryIt={() => {
            // TODO(#consumer-doc-qa-route): wire to `/documents/qa` once the
            //   chat surface ships. Today we route to the documents list as
            //   a safe fallback so the CTA is never a dead-end.
            navigate('/documents');
          }}
          onPromptClick={(promptKey) => {
            // TODO(#consumer-doc-qa-route): pre-fill the chat with the
            //   selected prompt key once the chat surface ships.
            console.info('[doc-qa] prompt clicked', promptKey);
            navigate('/documents');
          }}
        />
      </Stack>

      {/* Upload Dialog — confirmation surface; SplitPanel reserved for
          create/edit/detail per rule 14, this is a small attribute picker. */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploadMutation.isPending && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('documents.uploadDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {pendingFiles.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                {t('documents.uploadDialog.filesSelected', { count: pendingFiles.length })}
              </Typography>
            )}

            {pendingFiles.length === 0 && <DragDropZone onFiles={(files) => setPendingFiles(files)} />}

            <MagicSelect
              fullWidth
              label={t('documents.uploadDialog.selectCategory')}
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value as string)}
              options={DOCUMENT_CATEGORY_OPTIONS.map((cat) => ({
                value: cat.value,
                label: t(cat.labelKey),
              }))}
            />

            {!casesLoading && cases.length > 0 && (
              <MagicSelect
                fullWidth
                label={t('documents.uploadDialog.linkToCase')}
                value={uploadCaseId ?? ''}
                onChange={(e) => setUploadCaseId((e.target.value as string) || undefined)}
                options={[
                  { value: '', label: t('common.none') },
                  ...cases.map((c) => ({
                    value: c.id,
                    label: t('documents.caseLabel', { caseNumber: c.caseNumber, title: c.title }),
                  })),
                ]}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setUploadDialogOpen(false)} disabled={uploadMutation.isPending}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            variant="gradient"
            onClick={() => void handleUpload()}
            disabled={uploadMutation.isPending || pendingFiles.length === 0}
            startIcon={<UploadIcon />}
          >
            {uploadMutation.isPending
              ? t('documents.uploadDialog.uploading')
              : t('documents.uploadDialog.uploadFiles', { count: pendingFiles.length })}
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Link-to-case dialog — small attribute picker, Dialog is appropriate.
          Delete is handled from DocumentViewPage (existing flow), not from
          the row affordance — keeps rule 14 clean (Dialog only for confirms). */}
      <Dialog open={!!linkDocumentId} onClose={() => setLinkDocumentId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('documents.linkToCase.action')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('documents.linkToCase.select')}
            </Typography>
            {casesLoading ? (
              <Typography variant="body2" color="textSecondary">
                {t('common.loading')}
              </Typography>
            ) : cases.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {t('documents.view.noCases')}
              </Typography>
            ) : (
              <MagicSelect
                fullWidth
                label={t('documents.uploadDialog.linkToCase')}
                value={linkCaseId}
                onChange={(e) => setLinkCaseId(e.target.value as string)}
                options={cases.map((c) => ({
                  value: c.id,
                  label: t('documents.caseLabel', { caseNumber: c.caseNumber, title: c.title }),
                }))}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setLinkDocumentId(null)}>{t('common.cancel')}</GlassButton>
          <GlassButton
            variant="gradient"
            onClick={() => void handleLinkToCase()}
            disabled={!linkCaseId || linkToCaseMutation.isPending || cases.length === 0}
            startIcon={<LinkIcon />}
          >
            {linkToCaseMutation.isPending ? t('common.saving') : t('documents.linkToCase.action')}
          </GlassButton>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
}
